import React from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Row,
  Col,
} from "reactstrap";
import CoreEngine from "../../core/CoreEngine";
import RequestEngine from "../../core/RequestEngine";
import { connect } from "react-redux";
import loadingAction from "../../core/redux/actions/loadingAction";
import ReactTable from "react-table";
import TablePagination from "components/Table/TablePagination";
import strings from "../../core/translate";
import GarageSectionHeader from "../../components/Garage/GarageSectionHeader";
import RowActions, {
  editAction,
  deleteAction,
} from "components/Table/RowActions";
import SelectRow from "../../components/Select/SelectRow";
import { Scopes } from "../../shared/scopes.enum";
import WorkerReportModal from "./WorkerReportModal";

class MerchantWorkerList extends CoreEngine {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      dataSource: [],
      merchants: [],
      selectedMerchant: "",
      selectedBranch: "",
      branchName: "",
      globalFilter: "",
      startDateFilter: null,
      endDateFilter: null,
      canWrite: false,
      reportWorker: null,
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    const merchantIdFromStorage = sessionStorage.getItem("merchantId");
    const { selectedMerchant, selectedBranch } = this.props.match.params;
    const routeState = this.props.location.state || {};

    // reached from a garage record: this page is about that garage only
    if (selectedBranch && selectedBranch !== ":selectedBranch") {
      this.setState(
        {
          selectedBranch,
          branchName: routeState.branchName || "",
          selectedMerchant:
            merchantIdFromStorage || routeState.selectedMerchant || "",
          canWrite: this.hasPermission([Scopes.MERCHANTS_WORKERS_WRITE]),
        },
        () => {
          if (!this.state.selectedMerchant) {
            this.resolveMerchantFromBranch(selectedBranch);
          } else {
            this.callPage();
          }
        }
      );
      return;
    }
    const canWrite = this.hasPermission([Scopes.MERCHANTS_WORKERS_WRITE])
    this.setState({ canWrite });

    if (merchantIdFromStorage) {
      this.setState({ selectedMerchant: merchantIdFromStorage }, this.callPage);
      this.fetchMerchants();
    } else if (selectedMerchant !== ":selectedMerchant") {
      this.setState({ selectedMerchant }, this.callPage);
      this.fetchMerchants();
    } else {
      this.fetchMerchants();
    }
  }

  fetchMerchants = async () => {
    const response = await this.engine.getItem("merchants");
    if (response && response.status === 200) {
      this.setState({
        isLoading: false,
        merchants: response.data.items.map((merchant) => ({
          value: merchant.id,
          label: merchant.name,
        })),
      });
      if (this.state.merchants.length === 1) {
        this.setState({
          selectedMerchant: this.state.merchants[0].value,
        });
        this.callPage();
      }
    }
  };

  // the workers endpoint is per merchant, so find the merchant of this garage
  resolveMerchantFromBranch = async (branchId) => {
    const response = await this.engine.getItem(`merchants-branches/${branchId}`);
    const branch = response && response.data;
    if (branch && branch.merchant) {
      this.setState(
        {
          selectedMerchant: branch.merchant.id,
          branchName: this.state.branchName || branch.name || branch.address,
        },
        this.callPage
      );
    } else {
      this.setState({ isLoading: false });
    }
  };

  callPage = async () => {
    const { selectedMerchant, selectedBranch } = this.state;
    if (!selectedMerchant) return;

    const pathname = `merchants/${selectedMerchant}/workers`;
    const response = await this.engine.getItem(pathname);
    if (response && response.status === 200) {
      const items = selectedBranch
        ? response.data.items.filter(
            (item) => item.branch && item.branch.id === selectedBranch
          )
        : response.data.items;
      this.setState({
        isLoading: false,
        dataSource: items.map((item, key) => ({
          id: key,
          data: item,
          name: item.name,
          title: item.title,
          phoneNumber: item.phoneNumber,
          username: item.username || "—",
          branch: item.branch?.address || item.branch?.name || "—",
          canAccessApp: item.canAccessApp ? strings.yes : strings.no,
          role: strings[`role_${item.role || "mechanic"}`],
          tier: item.tier ? item.tier.name : "—",
          created: new Date(item.createdAt),
          actions: this.handleActions(key, item),
        })),
      });
    }
    this.props.loadingAction(false);
  };

  handleMerchantSelect = (event) => {
    const selectedMerchant = event.target.value;
    this.setState({ selectedMerchant }, () => {
      this.props.history.push(`/admin/merchant/${selectedMerchant}/branches`);
      this.callPage();
    });
  };

  handleAdd = () => {
    const { selectedMerchant, selectedBranch, branchName } = this.state;
    if (!selectedMerchant) {
      alert(strings.select + " " + strings.merchant);
      return;
    }
    // opened from a garage: the new worker belongs to it, no picker needed
    this.props.history.push({
      pathname: `/admin/merchant/${selectedMerchant}/workers/add`,
      state: { selectedBranch, branchName },
    });
  };

  handlePage() {
    this.props.loadingAction(true);
    this.callPage();
  }

  async handleDelete(valueid) {
    if (window.confirm(strings.confirmdelete)) {
      this.props.loadingAction(true);
      const { selectedMerchant } = this.state;
      const result = await this.engine.deleteItem(
        `merchants/${selectedMerchant}/workers`,
        valueid
      );
      if (result) {
        this.handlePage();
      }
      this.props.loadingAction(false);
    }
  }

  handleEdit(index, dataid) {
    const data = this.state.dataSource[index].data;
    const { selectedMerchant, selectedBranch, branchName } = this.state;

    this.props.history.push({
      pathname: `/admin/merchant/${selectedMerchant}/workers/edit/${dataid}`,
      state: { selectedBranch, branchName },
      data,
    });
  }

  handleActions(index, item) {
    return (
      <RowActions
        actions={[
          {
            key: "report",
            icon: "fa fa-bar-chart",
            label: strings.report_monthly,
            onClick: () => this.setState({ reportWorker: item }),
          },
          editAction(() => this.handleEdit(index, item.id), this.state.canWrite),
          deleteAction(() => this.handleDelete(item.id), this.state.canWrite),
        ]}
      />
    );
  }

  render() {
    const {
      dataSource,
      isLoading,
      merchants,
      selectedMerchant,
      selectedBranch,
      branchName,
      canWrite,
      reportWorker,
    } = this.state;

    let columns = [
      {
        Header: strings.name,
        accessor: "name",
      },
      {
        Header: strings.title,
        accessor: "title",
      },
      {
        Header: strings.phonenumber || "Phone",
        accessor: "phoneNumber",
      },
      {
        Header: strings.login_username,
        accessor: "username",
      },
      {
        Header: strings.branch || "Branch",
        accessor: "branch",
      },
      {
        Header: strings.staff_role,
        accessor: "role",
      },
      {
        Header: strings.staff_tier,
        accessor: "tier",
      },
      {
        Header: strings.access_app,
        accessor: "canAccessApp",
      },
      {
        Header: strings.created,
        accessor: "created",
        Cell: (row) => row.value.toLocaleDateString(),
      },
      {
        Header: strings.actions,
        accessor: "actions",
        sortable: false,
        filterable: false,
      },
    ];

    return (
      <>
        <div className="content">
          <GarageSectionHeader
            branchId={selectedBranch}
            branchName={branchName}
            merchantId={selectedMerchant}
            active="workers"
            history={this.props.history}
          />
          <Row>
            <Col md="12">
              <Card>
                <CardHeader>
               {canWrite && (   <Button
                    color="primary"
                    className="pull-right"
                    onClick={this.handleAdd}
                  >
                    {strings.add}
                  </Button>)}
                  {!sessionStorage.getItem("merchantId") &&
                    merchants.length !== 1 && (
                      <SelectRow
                        label={strings.merchant}
                        name="selectedMerchant"
                        defaultValue={selectedMerchant}
                        data={merchants}
                        changeInput={(e) => {
                          const selectedMerchant = e.value;
                          this.setState({ selectedMerchant }, () => {
                            this.props.history.push(
                              `/admin/merchant/${selectedMerchant}/workers`
                            );
                            this.callPage();
                          });
                        }}
                        placeholder={strings.select}
                      />
                    )}
                </CardHeader>
                <CardBody>
                  <ReactTable
                    previousText={strings.previous}
                    nextText={strings.next}
                    loadingText={strings.loading}
                    noDataText={strings.nodata}
                    pageText={strings.page}
                    data={dataSource}
                    defaultFilterMethod={this.filterCaseInsensitive}
                    filterable
                    loading={isLoading}
                    columns={columns}
                    defaultPageSize={10}
                    showPaginationTop={false}
                    showPaginationBottom
                    PaginationComponent={TablePagination}
                    className="-striped -highlight primary-pagination"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <WorkerReportModal
            worker={reportWorker}
            merchantId={selectedMerchant}
            history={this.props.history}
            onClose={() => this.setState({ reportWorker: null })}
          />
        </div>
      </>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  loadingAction: (payload) => dispatch(loadingAction(payload)),
});

export default connect(null, mapDispatchToProps)(MerchantWorkerList);
